/**
 * PreviousOrderPageViewModel - Shared Previous Order Page ViewModel
 *
 * Maps a single Order to PageContentPreviousOrderProps / SectionPreviousOrderSummaryProps.
 *
 * Cross-platform implementation with dependency injection.
 */

import { useMemo } from 'react'
import type { IPlatformServices } from '../contracts'
import type {
  Order,
  OrderItem,
  SelectedOption,
  PriceBreakdown,
  PageContentPreviousOrderProps,
  PreviousOrderCartItem,
  PreviousOrderLineItem,
} from '@restaurant/core'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a number as US currency string: "$X.XX"
 */
function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`
}

/**
 * Formats selected options into a human-readable string.
 * Each option group's selected items are joined, then groups are separated by "; ".
 *
 * Example: "Size: Large; Toppings: Extra Cheese, Mushrooms"
 */
function formatSelectedOptions(selectedOptions: SelectedOption[]): string | undefined {
  if (!selectedOptions || selectedOptions.length === 0) return undefined

  const parts = selectedOptions
    .map((opt) => {
      const items = opt.selectedItems.map((item) => item.name).join(', ')
      if (!items) return null
      return opt.optionTitle ? `${opt.optionTitle}: ${items}` : items
    })
    .filter(Boolean)

  return parts.length > 0 ? parts.join('; ') : undefined
}

/**
 * Derives an order number string from the order.
 * Prefers externalId; falls back to last 6 characters of the internal id.
 */
function getOrderNumber(order: Order): string {
  if (order.externalId) return order.externalId
  return order.id.slice(-6)
}

/**
 * Builds the title string for an order.
 * "Pickup from <locationName>" or "Delivery to <street>".
 */
function getOrderTitle(order: Order, locationName: string): string {
  if (order.isDelivery && order.deliveryAddress) {
    return `Delivery to ${order.deliveryAddress.street}`
  }
  return `Pickup from ${locationName}`
}

/**
 * Builds the location address string.
 * For pickup: location name. For delivery: the full delivery address street.
 */
function getLocationAddress(order: Order, locationName: string): string {
  if (order.isDelivery && order.deliveryAddress) {
    return order.deliveryAddress.street
  }
  return locationName
}

// ---------------------------------------------------------------------------
// Pure mapping functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Maps a single OrderItem to PreviousOrderCartItem.
 */
export function mapOrderItemToCartItem(item: OrderItem): PreviousOrderCartItem {
  const productOptions = formatSelectedOptions(item.selectedOptions)

  return {
    productName: item.menuItem.name,
    priceCurrent: formatCurrency(item.menuItem.price * item.quantity),
    quantity: item.quantity,
    productOptions,
    showProductOptions: Boolean(productOptions),
    showPriceOld: false,
  }
}

/**
 * Builds line items from a PriceBreakdown.
 * Always includes Subtotal and Fees & Tax.
 * Conditionally includes Delivery Fee, Tip, and Discount.
 */
export function buildLineItems(breakdown: PriceBreakdown): PreviousOrderLineItem[] {
  const lineItems: PreviousOrderLineItem[] = [
    { label: 'Subtotal', amount: formatCurrency(breakdown.subtotal) },
  ]

  if (breakdown.deliveryFee > 0) {
    lineItems.push({ label: 'Delivery Fee', amount: formatCurrency(breakdown.deliveryFee) })
  }

  lineItems.push({ label: 'Fees & Tax', amount: formatCurrency(breakdown.feesAndTaxes) })

  if (breakdown.driverTip > 0) {
    lineItems.push({ label: 'Tip', amount: formatCurrency(breakdown.driverTip) })
  }

  if (breakdown.offerDiscount > 0) {
    lineItems.push({ label: 'Discount', amount: `-${formatCurrency(breakdown.offerDiscount)}` })
  }

  return lineItems
}

/**
 * Calculates the total from a PriceBreakdown.
 */
export function calculateTotal(breakdown: PriceBreakdown): number {
  return (
    breakdown.subtotal +
    breakdown.deliveryFee +
    breakdown.feesAndTaxes +
    breakdown.driverTip -
    breakdown.offerDiscount
  )
}

/**
 * Maps a full Order to PageContentPreviousOrderProps.
 */
export function mapOrderToPreviousOrderProps(
  order: Order,
  locationName: string,
  onReorder?: () => void,
): PageContentPreviousOrderProps {
  const items = order.items.map(mapOrderItemToCartItem)
  const lineItems = buildLineItems(order.priceBreakdown)
  const total = calculateTotal(order.priceBreakdown)

  return {
    orderNumber: getOrderNumber(order),
    title: getOrderTitle(order, locationName),
    locationAddress: getLocationAddress(order, locationName),
    items,
    lineItems,
    total: formatCurrency(total),
    buttonText: 'Re-Order Items',
    onReorder,
  }
}

// ---------------------------------------------------------------------------
// Dependencies & return types
// ---------------------------------------------------------------------------

export interface PreviousOrderPageViewModelDependencies {
  /** Looks up a single order by id */
  getOrderById: (orderId: string) => Order | undefined
  /** Resolves a merchantId to a human-readable location name */
  getLocationName: (merchantId: string) => string
  /** Called when re-order button is pressed */
  onReorder: (orderId: string) => void
  /** Loading state */
  loading: boolean
}

export interface PreviousOrderPageViewModelReturn {
  /** Props for PageContentPreviousOrder */
  getPageContentPreviousOrderProps: (orderId: string) => Partial<PageContentPreviousOrderProps> | null
  /** Whether data is loading */
  loading: boolean
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Factory that returns a React hook for the Previous Order page.
 *
 * Usage:
 * ```ts
 * const usePreviousOrderPage = createPreviousOrderPageViewModel(services, deps)
 * // inside a component:
 * const vm = usePreviousOrderPage()
 * ```
 */
export const createPreviousOrderPageViewModel = (
  _services: IPlatformServices,
  dependencies: PreviousOrderPageViewModelDependencies,
) => {
  return (): PreviousOrderPageViewModelReturn => {
    const {
      getOrderById,
      getLocationName,
      onReorder,
      loading,
    } = dependencies

    const getPageContentPreviousOrderProps = useMemo(
      () =>
        (orderId: string): Partial<PageContentPreviousOrderProps> | null => {
          const order = getOrderById(orderId)
          if (!order) return null

          const props = mapOrderToPreviousOrderProps(
            order,
            getLocationName(order.merchantId),
            () => onReorder(orderId),
          )

          return {
            ...props,
            loading,
          }
        },
      [getOrderById, getLocationName, onReorder, loading],
    )

    return {
      getPageContentPreviousOrderProps,
      loading,
    }
  }
}
